import React from "react";
import { ICON_SIZES } from "./constants";
import type { PodcastIconProps } from "./types";

/**
 * PodcastIcon Component
 *
 * Renders a single podcast platform icon from a dynamic URL (loaded from DatoCMS).
 * This is a presentational-only component that accepts an icon URL and renders it as an img tag.
 *
 * @component
 * @example
 * // Render Spotify icon with custom sizing
 * <PodcastIcon
 *   iconUrl="https://www.datocms-assets.com/60124/1773303803-spotify-logo.svg"
 *   platform="spotify"
 *   className="h-[24px] w-[24px]"
 *   ariaLabel="Listen on Spotify"
 * />
 *
 * @param props - Component props
 * @returns React element rendering the icon image
 */
export const PodcastIcon: React.FC<PodcastIconProps> = ({
  iconUrl,
  platform,
  className,
  ariaLabel,
}) => {
  const iconClassName = className || ICON_SIZES.MEDIUM;

  const extractSize = (cls: string) => {
    const matches = cls.match(/[wh]-\[(\d+)px\]/g);
    if (matches && matches[0]) {
      return parseInt(matches[0].match(/\d+/)?.[0] || "24", 10);
    }
    return 24;
  };

  const size = extractSize(iconClassName);

  return (
    <img
      src={iconUrl}
      alt={ariaLabel || platform}
      className={iconClassName}
      width={size}
      height={size}
      data-testid={`podcast-icon-${platform}`}
    />
  );
};

PodcastIcon.displayName = "PodcastIcon";

export default PodcastIcon;
